import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import { ApiError, facilityKeys } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { deviceInstallationKeys } from "./use-device-installations";

type Violation = components["schemas"]["Violation"];
type ViolationListResponse = components["schemas"]["ViolationListResponse"];
type ViolationType = components["schemas"]["ViolationType"];
type ViolationOrigin = components["schemas"]["ViolationOrigin"];
type ViolationDiscoveryMethod = components["schemas"]["ViolationDiscoveryMethod"];
type ViolationResponsibleParty = components["schemas"]["ViolationResponsibleParty"];
type ViolationStatus = components["schemas"]["ViolationStatus"];
type ViolationEvidenceKind = components["schemas"]["ViolationEvidenceKind"];
type ViolationCreateRequest = components["schemas"]["ViolationCreateRequest"];
type ViolationUpdateRequest = components["schemas"]["ViolationUpdateRequest"];
type EnforcementAction = components["schemas"]["EnforcementAction"];
type EnforcementActionType = components["schemas"]["EnforcementActionType"];
type EnforcementActionCreateRequest = components["schemas"]["EnforcementActionCreateRequest"];
type ViolationRecordItem = components["schemas"]["ViolationRecordItem"];
type ViolationRecordKind = components["schemas"]["ViolationRecordKind"];
type ViolationRecordListResponse = components["schemas"]["ViolationRecordListResponse"];

// Cache windows follow the field-credentials canon: short list staleness so
// fresh writes appear quickly, with keepPreviousData smoothing pagination.
const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;
const DETAIL_STALE_TIME = 2 * 60_000;
const DETAIL_GC_TIME = 10 * 60_000;

/** Mirrors the listViolations OpenAPI sort_by enum (the Go ORDER BY allow-list). */
export type ViolationSortBy =
  | "discovered_date"
  | "correction_due_date"
  | "status"
  | "violation_type"
  | "origin"
  | "responsible_party"
  | "created_at"
  | "facility_name"
  | "hazard_type_label";

export interface ViolationsListParams {
  status?: ViolationStatus;
  violation_type?: ViolationType;
  origin?: ViolationOrigin;
  responsible_party?: ViolationResponsibleParty;
  facility_id?: string;
  /** Cases anchored to one device installation (the device-detail Compliance tab). */
  device_installation_id?: string;
  /** Cases opened from one accepted submission (the submission page's Violations card). */
  form_submission_id?: string;
  /** Cases naming this org tester as the responsible tester. */
  org_tester_id?: string;
  /** Cases naming this hazard type. */
  hazard_type_id?: string;
  /**
   * When true, only cases in the open set (open / in_correction /
   * termination_pending / service_discontinued / appealed).
   */
  open?: boolean;
  /**
   * When true, drops rescinded cases without narrowing to one status. Absent
   * includes them — the wire's permissive default.
   */
  exclude_rescinded?: boolean;
  /** Inclusive lower bound on discovered_date. */
  discovered_from?: string;
  /** Inclusive upper bound on discovered_date. */
  discovered_to?: string;
  /** Inclusive lower bound on correction_due_date. */
  due_from?: string;
  /** Inclusive upper bound on correction_due_date. */
  due_to?: string;
  sort_by?: ViolationSortBy;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

/** The record's one page: the server's default and every item a case carries. */
const RECORD_LIMIT = 100;

/**
 * Query-key factory for the enforcement case caches. Org-param routes thread
 * orgId through every concrete key. Every case lives under `all`, so one
 * prefix invalidation refreshes the list, the case file, its derived record
 * and its audit trail together. The assignment-side promotion verb invalidates
 * the list cross-entity: a promotion opens a case (origin
 * `promoted_work_assignment`) that must appear on the violations surface
 * without a manual refresh. Every other case opens through the create — by
 * hand, or from an accepted submission (origin `promoted_form_submission`) —
 * and rides the same prefix.
 */
export const violationKeys = {
  all: ["violations"] as const,
  lists: () => [...violationKeys.all, "list"] as const,
  list: (orgId: string, params: ViolationsListParams) =>
    [...violationKeys.lists(), orgId, params] as const,
  detail: (orgId: string, id: string) => [...violationKeys.all, "detail", orgId, id] as const,
  record: (orgId: string, id: string) => [...violationKeys.all, "record", orgId, id] as const,
  activity: (orgId: string, id: string) => [...violationKeys.all, "activity", orgId, id] as const,
} as const;

function withDefaults(params: ViolationsListParams): ViolationsListParams {
  return {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
}

/** The premise and assembly a case sits on — the surfaces its writes reach. */
interface CaseAnchor {
  facility_id: string;
  device_installation_id?: string | null;
}

/**
 * Every surface a case write changes. `violationKeys.all` covers the list, the
 * case file, its derived record and its audit trail (each write emits one
 * audit row, so the feed on the page that caused it must refetch). The
 * device's related counts carry `open_violations_count` behind the Compliance
 * tab chip; the premise and device detail reads refetch with them so the pages
 * hosting the two compliance tabs never disagree with the case they just
 * wrote.
 */
function invalidateCaseSurfaces(queryClient: QueryClient, orgId: string, anchor: CaseAnchor) {
  void queryClient.invalidateQueries({ queryKey: violationKeys.all });
  void queryClient.invalidateQueries({
    queryKey: facilityKeys.detail(orgId, anchor.facility_id),
  });
  const deviceId = anchor.device_installation_id;
  if (deviceId) {
    void queryClient.invalidateQueries({ queryKey: deviceInstallationKeys.detail(deviceId) });
    void queryClient.invalidateQueries({
      queryKey: deviceInstallationKeys.relatedCounts(deviceId),
    });
  }
}

/** Paginated org case list with the resolved facility_name label on every row.
 * `enabled: false` holds the request — a caller without `violations:read`, or
 * with no facility or device chosen yet, never issues it. */
export function useViolations(
  params: ViolationsListParams = {},
  { enabled = true }: { enabled?: boolean } = {},
) {
  const orgId = useAppStore((s) => s.activeOrgId);
  const merged = withDefaults(params);
  return useQuery<ViolationListResponse>({
    queryKey: violationKeys.list(orgId ?? "", merged),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/violations", {
        params: { path: { orgId }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list violations";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: enabled && Boolean(orgId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/** The one page a submission's cases read: a submission opens a handful of cases, never a register's worth. */
const SUBMISSION_CASES_LIMIT = 50;

/**
 * The cases opened from one accepted submission — one explicitly bounded page,
 * most recently discovered first. The submission page's cases card and its
 * inspection letter drawer read it under the same key, so the drawer's
 * preselection reads the cases the card shows. `{ enabled: false }` holds the
 * request — the caller lacks `violations:read`.
 */
export function useSubmissionViolations(submissionId: string, options: { enabled: boolean }) {
  return useViolations(
    {
      form_submission_id: submissionId,
      limit: SUBMISSION_CASES_LIMIT,
      sort_by: "discovered_date",
      sort_dir: "desc",
    },
    options,
  );
}

/** One case. */
export function useViolation(id: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<Violation>({
    queryKey: violationKeys.detail(orgId ?? "", id ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!id) throw new Error("Violation id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/violations/{violationId}",
        {
          params: { path: { orgId, violationId: id } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load violation";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(id),
    staleTime: DETAIL_STALE_TIME,
    gcTime: DETAIL_GC_TIME,
  });
}

/**
 * Open a case by hand. A typed 422 reaches the caller as an `ApiError` whose
 * `code` is the server's (`violationErrorMessage` renders it).
 */
export function useCreateViolation() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: ViolationCreateRequest): Promise<Violation> => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST("/orgs/{orgId}/violations", {
        params: { path: { orgId } },
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to create violation";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: (data) => {
      if (orgId) invalidateCaseSurfaces(queryClient, orgId, data);
    },
  });
}

/**
 * The lifecycle verb and the clerical edits, one PATCH. No optimistic merge:
 * a transition's server-side effects (the derived counters, `held_from_status`)
 * cannot be predicted here, so the case file shows only a state the server
 * reached.
 */
export function useUpdateViolation(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: ViolationUpdateRequest): Promise<Violation> => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.PATCH(
        "/orgs/{orgId}/violations/{violationId}",
        {
          params: { path: { orgId, violationId: id } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to update violation";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: (data) => {
      if (orgId) invalidateCaseSurfaces(queryClient, orgId, data);
    },
  });
}

/**
 * The case's derived record, newest first, in the order the server returns
 * it: the opening, the letters that went out, the status moves, the work
 * bound to the case and its completion, and the noted acts of enforcement.
 * One page of the server's default size — the whole record of a case.
 */
export function useViolationRecord(id: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<ViolationRecordListResponse>({
    queryKey: violationKeys.record(orgId ?? "", id ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!id) throw new Error("Violation id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/violations/{violationId}/record",
        {
          params: { path: { orgId, violationId: id }, query: { limit: RECORD_LIMIT } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load the violation record";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(id),
    staleTime: DETAIL_STALE_TIME,
    gcTime: DETAIL_GC_TIME,
  });
}

/**
 * Record an act of enforcement against this case (and, through
 * `additional_violation_ids`, its sibling cases on the same premises). The
 * action row carries its facility but not the case's device, so the device
 * half of the invalidation set reads the case the page already holds.
 */
export function useRecordEnforcementAction(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: EnforcementActionCreateRequest): Promise<EnforcementAction> => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/violations/{violationId}/enforcement-actions",
        {
          params: { path: { orgId, violationId: id } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to record enforcement action";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: (data) => {
      if (!orgId) return;
      const held = queryClient.getQueryData<Violation>(violationKeys.detail(orgId, id));
      invalidateCaseSurfaces(queryClient, orgId, {
        facility_id: data.facility_id,
        device_installation_id: held?.device_installation_id,
      });
    },
  });
}

export type {
  Violation,
  ViolationListResponse,
  ViolationType,
  ViolationOrigin,
  ViolationDiscoveryMethod,
  ViolationResponsibleParty,
  ViolationStatus,
  ViolationEvidenceKind,
  ViolationCreateRequest,
  ViolationUpdateRequest,
  EnforcementAction,
  EnforcementActionType,
  EnforcementActionCreateRequest,
  ViolationRecordItem,
  ViolationRecordKind,
  ViolationRecordListResponse,
};
